import React, { useContext, useState, useEffect } from "react";
import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Alert,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import CloseIcon from "@mui/icons-material/Close";
import { FormRowStyle, FormInputStyle } from "../utils/AAStyles";
import { useTheme } from "@mui/material/styles";
import { UserContext } from "../context/userContext";
import { AuthenticateUser, ResetMyPassword } from "../configuration/AAConfig_Security";

interface AuthenticateDialogProps {
  title: string;
  message: string;
  mode: "login" | "reset";
  onSuccess?: () => void;
}

const AuthenticateDialog: React.FC<AuthenticateDialogProps> = ({ title, message, mode, onSuccess }) => {
  const theme = useTheme();
  const {
    authenticateId,
    updateAuthenticateId,
    updateShowAuthenticate,
    updateLoginDetails,
    showAuthenticate,
    userError,
    updateUseAuthenticate,
  } = useContext(UserContext);

  const [authenticateCode, setAuthenticationCode] = useState<string>("");
  const [authenticateError, setAuthenticateError] = useState<string | null>(userError);
  const [step, setStep] = useState<1 | 2>(mode === "reset" ? 2 : 1);
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const isResetMode = mode === "reset";

  const handleAuthenticateCodeChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setAuthenticationCode(e.target.value);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setNewPassword(e.target.value);

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setConfirmPassword(e.target.value);

  const IsCodeValid = (): boolean => {
    return !!authenticateId && !!authenticateCode;
  };

  const handleAuthenticateClick = async () => {
    if (!IsCodeValid()) {
      setAuthenticateError("Authentication or Authorization is empty.");
      return;
    }

    if (isResetMode && step === 1) {
      setStep(2);
      return;
    }

    if (isResetMode && step === 2) {
      if (!newPassword || !confirmPassword) {
        setAuthenticateError("Password fields cannot be empty.");
        return;
      }
      if (newPassword !== confirmPassword) {
        setAuthenticateError("Passwords do not match.");
        return;
      }

      try {
        await ResetMyPassword({
          resetId: authenticateId,
          code: authenticateCode,
          password: newPassword,
          confirmPassword: confirmPassword,
        });
        updateShowAuthenticate(false);
        onSuccess?.();
      } catch (err) {
        setAuthenticateError("Password reset failed. Please check the code and try again.");
      }
      return;
    }

    // Login flow
    try {
      const loginRes = await AuthenticateUser(authenticateId!, authenticateCode, setAuthenticateError);
      if (!loginRes) {
        setAuthenticateError("Unable to login with the supplied code.");
      } else {
        updateLoginDetails(loginRes);
        updateShowAuthenticate(false);
        onSuccess?.();
      }
    } catch (err) {
      setAuthenticateError("Login failed.");
    }
  };

  const handleCancelClick = () => {
    setAuthenticateError(null);
    updateAuthenticateId(null);
    updateShowAuthenticate(false);
    setStep(1);
  };

  useEffect(() => {
    setAuthenticateError(userError);
  }, [userError]);

  useEffect(() => {
    updateUseAuthenticate(true);
  }, [updateUseAuthenticate]);

  return (
    <Dialog
      open={showAuthenticate}
      aria-labelledby="authenticate-dialog"
      fullWidth
      maxWidth="xs"
      disableEnforceFocus
    >
      <DialogTitle id="authenticate-dialog" sx={{ color: "#fff", backgroundColor: "#2A6EBB" }}>
        {title || "Authenticate"}
      </DialogTitle>
      <DialogContent sx={{ marginTop: theme.spacing(1) }}>
        {authenticateError && <Alert severity="error">{authenticateError}</Alert>}
        {!authenticateError && <Typography variant="body1">{message}</Typography>}

        <Grid container justifyContent="flex-start" alignItems="center" sx={FormRowStyle()}>
          <Grid item xs={6}>
            <Typography variant="body2" align="left">Authentication Code*</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              sx={FormInputStyle()}
              fullWidth
              required
              variant="outlined"
              margin="dense"
              size="small"
              value={authenticateCode}
              onChange={handleAuthenticateCodeChange}
            />
          </Grid>

          {isResetMode && step === 2 && (
            <>
              <Grid item xs={6}>
                <Typography variant="body2" align="left">New Password*</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  sx={FormInputStyle()}
                  type="password"
                  fullWidth
                  required
                  variant="outlined"
                  margin="dense"
                  size="small"
                  value={newPassword}
                  onChange={handlePasswordChange}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography variant="body2" align="left">Confirm Password*</Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  sx={FormInputStyle()}
                  type="password"
                  fullWidth
                  required
                  variant="outlined"
                  margin="dense"
                  size="small"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ marginBottom: theme.spacing(1) }}>
        <Button
          onClick={handleAuthenticateClick}
          autoFocus
          variant="contained"
          sx={{
            color: "#FFFFFF",
            backgroundColor: "#2A6EBB",
            "&:hover": { backgroundColor: "#00AEEF", color: "#FFFFFF" },
          }}
          startIcon={<LoginIcon />}
        >
          {isResetMode ? (step === 1 ? "Next" : "Reset Password") : "Authenticate"}
        </Button>
        <Button
          onClick={handleCancelClick}
          sx={{
            color: "#2A6EBB",
            "&:hover": { backgroundColor: "#00AEEF", color: "#FFFFFF" },
          }}
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuthenticateDialog;
